// WP-1.14 (OB-4, S-13 `/accounts/:id/reconcile`): reconciliation snapshot + Adjustment.
import {
  adjustmentCreateRequestSchema,
  reconciliationResponseSchema,
  transactionResponseSchema,
  type AdjustmentCreateRequest,
  type ReconciliationResponse,
  type TransactionResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';
import { TRANSACTIONS_KEY } from './transactions';

export const RECONCILE_KEY = ['reconcile'] as const;

export function useReconciliation(
  accountId: string,
  statedBalanceMinor: number | undefined,
): UseQueryResult<ReconciliationResponse> {
  return useQuery({
    queryKey: [...RECONCILE_KEY, accountId, statedBalanceMinor],
    queryFn: async () =>
      reconciliationResponseSchema.parse(
        await api.get(
          `/api/v1/accounts/${accountId}/reconcile?statedBalanceMinor=${String(statedBalanceMinor)}`,
        ),
      ),
    enabled: statedBalanceMinor !== undefined,
  });
}

export function useRecordAdjustment(
  accountId: string,
): UseMutationResult<TransactionResponse, Error, AdjustmentCreateRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      transactionResponseSchema.parse(
        await api.post(
          `/api/v1/accounts/${accountId}/reconcile/adjust`,
          adjustmentCreateRequestSchema.parse(input),
        ),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
        queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: [...RECONCILE_KEY, accountId] }),
      ]);
    },
  });
}
